import { useContext, useEffect, useRef } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { PageIndex } from "../../state/page";
import { ScrollTriggerPosition } from "../../state/video";
import { StickyContext } from "../../displayComponents/displayElements/Sticky";
import type { TapAnimationEffectProps } from "./types";
import gsap from "gsap";

function useTapAnimation({ elRef, direction, pageIndex, numberOfPages, isInAuthor, isPreviewer, navigationType, position, isInPlayWrightTest }: TapAnimationEffectProps) {
    const left = position === "left";
    const right = position === "right"
    const forward = direction === "forward"
    const back = direction === "back"
    const currentPageIndex = useRecoilValue(PageIndex) // use this to trigger the useEffect for animations after init
    const isInSticky = useContext(StickyContext)
    const setScrollTriggerProgress = useSetRecoilState(ScrollTriggerPosition)
    // panes only animate on page transitions; a tap that reveals elements
    // in-page flips the direction atom without changing the page and must not
    // replay the exit/enter tweens (it used to slide the old page across the
    // screen again, and on page 1 it hid the only rendered pane)
    const lastAnimatedPageRef = useRef(-1)

    //tap animations
    useEffect(() => {
        if (numberOfPages === 1) return;
        if (isPreviewer || isInPlayWrightTest || isInAuthor || isInSticky) return;
        if (navigationType !== "tap") return
        const pageChanged = lastAnimatedPageRef.current !== currentPageIndex
        lastAnimatedPageRef.current = currentPageIndex
        if (!pageChanged) return
        let ctx: gsap.Context
        const timeline = gsap.timeline({
            onComplete: () => {
                if (
                    (forward && right) || // Navigating forward, this is the incoming page
                    (back && left)       // Navigating backward, this is the incoming page
                ) {
                    const contentToFocus = document.getElementById(`display-elements-children-${pageIndex}`);
                    if (contentToFocus) {
                        /* land on the slide's first heading so a screen reader
                           announces where it arrived and read-all continues
                           from the top; fall back to the container */
                        const heading = contentToFocus.querySelector<HTMLElement>(
                            "h1, h2, h3, [role=heading]",
                        );
                        if (heading && !heading.hasAttribute("tabindex")) {
                            heading.setAttribute("tabindex", "-1");
                        }
                        (heading ?? contentToFocus).focus({ preventScroll: true });
                    }
                }
            }
        })
        timeline.eventCallback("onUpdate", () => {
            setScrollTriggerProgress(timeline.progress())
        })
        if (elRef.current) {
            ctx = gsap.context(() => {
                if (forward) {
                    if (left) {
                        timeline.fromTo(elRef.current, { x: 0, autoAlpha: 1 }, { x: "-200%", autoAlpha: 0, ease: "sine.in" })
                    }
                    if (right) {
                        timeline.call(() => {
                            elRef.current?.removeAttribute("aria-hidden");
                        }, [], "<");
                        timeline.from(elRef.current, { x: "200%", autoAlpha: 0, ease: "sine.in" }, "<")

                    }
                }

                if (back) {
                    if (left) {
                        timeline.call(() => {
                            elRef.current?.removeAttribute("aria-hidden");
                        });
                        timeline.fromTo(elRef.current, { x: "-200%", autoAlpha: 0 }, { x: 0, autoAlpha: 1, ease: "sine.in" })
                    }
                    if (right) {
                        timeline.to(elRef.current, { x: "200%", autoAlpha: 0, ease: "sine.in" })

                    }
                }
            })
        }


        return () => {
            if (ctx) {
                ctx.revert();
            }
        };
    }, [elRef, navigationType, numberOfPages, isInAuthor, pageIndex, isPreviewer, isInPlayWrightTest, isInSticky, currentPageIndex, forward, back, left, right, setScrollTriggerProgress])
}


export default useTapAnimation